import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import {
  MDBBadge,
  MDBBtn,
  MDBIcon,
  MDBModal,
  MDBModalBody,
  MDBModalFooter,
  MDBModalHeader,
  MDBTable,
} from "mdbreact";
import { Services } from "../../../../../../../services/fakeDb";
import { GenericProducts } from "../../../../../../../constants";
import { axioKit, fullName } from "../../../../../../../services/utilities";
import {
  UTILIZATION_URL,
  UTILIZATION_DEPARTMENT,
  UTILIZATION_PHASES,
} from "../../../../catalog/preAnalytical";
import getSetupPath from "../../../../catalog/preAnalytical/setupPath";
import {
  autoSelect,
  brandsOf,
  onHandOf,
  stockKey,
} from "../../../../catalog/brandedProducts/select";
import { PURCHASE_URL } from "../../../../purchaseOrders/common";
import {
  breakdownOf,
  levelsOf,
  packSizeOf,
  packUnitOf,
} from "../../../../catalog/products/packagesEditor";

const url = "diagnostics/consumptions";
const SUPPLIERS_URL = "assets/providers/filterByCategory";

/**
 * Generic supplies of the tests (tagging): service -> generic -> default
 * brand/variant + units. A generic shared by tests is listed once with the
 * highest quantity and the brand/variant of the first test that tagged one.
 * Reusable supplies are dispensed monthly, never per patient.
 */
const suggestGenerics = (
  services = [],
  utilizations = [],
  phase = "preAnalytical",
) => {
  const generics = {};
  services.forEach(({ id, abbreviation, name }) => {
    const { phases = {} } =
      utilizations.find(({ serviceId }) => serviceId === id) || {};
    (phases[phase] || []).forEach(
      ({ pgid, product, variant, quantity = 1 }) => {
        const generic = GenericProducts.find(pgid);
        if (!generic || generic.reusable) return;
        const entry = generics[pgid] || {
          generic,
          tag: {},
          qty: 0,
          for: [],
          serviceIds: [],
        };
        if (!entry.tag.product && product) entry.tag = { product, variant };
        entry.qty = Math.max(entry.qty, quantity);
        entry.for.push(abbreviation || name);
        entry.serviceIds.push(id);
        generics[pgid] = entry;
      },
    );
  });
  return Object.values(generics);
};

// every brand + variant of a generic
const optionsOf = (products, pgid) =>
  brandsOf(products, pgid).flatMap((product) =>
    product.variants?.length
      ? product.variants.map((variant) => ({ product, variant }))
      : [{ product, variant: null }],
  );

// the default brand/variant of every generic gets its units, the others 0
const defaultQtys = (generics, products, stock) =>
  generics.reduce((result, { generic, tag, qty }) => {
    const { product, variant } = autoSelect(products, generic.id, tag);
    if (product && onHandOf(stock, product, variant) >= qty)
      result[stockKey(product, variant)] = qty;
    return result;
  }, {});

/**
 * The shelf of a brand + variant as read from its packs (server /shelf):
 * packs in all (total), the open ones (in use) with the units left in them,
 * and the sealed ones (remaining). Items not in packs are counted by unit.
 */
// in units (pcs, or tests): available = in use (left in open packs) + remaining (sealed)
const shelfView = (entry = {}, packQty) => {
  const available = Math.max(entry.onHand || 0, 0);
  if (packQty <= 1)
    return {
      available,
      inUse: 0,
      remaining: available,
      openPacks: 0,
      sealedPacks: 0,
      openLeft: 0,
    };
  const inUse = Math.min(entry.openLeft || 0, available);
  return {
    available,
    inUse,
    remaining: available - inUse,
    openPacks: entry.open || 0,
    sealedPacks: entry.sealed || 0,
    openLeft: inUse,
  };
};

// packs opened to take `qty` units: the open ones are used first, then FIFO
const packsToOpen = (qty, { openLeft }, packQty) =>
  packQty <= 1 || qty <= openLeft ? 0 : Math.ceil((qty - openLeft) / packQty);

/**
 * Records the pre-analytical supplies (syringe, tubes, cotton, etc.) used on
 * the patient of this deal: every brand + variant of the supplies tagged to
 * the tests, in units (pcs, or tests). The default starts with its units, the
 * others at 0; staff may change any of them (e.g. 3cc and 5cc both 1).
 * Units come from the opened pack first; a sealed pack is opened when needed.
 * `phase`: the same for the analytical supplies (reagents, cartridges) from
 * Tasks, or the post-analytical ones.
 */
export default function PreAnalytical({
  show,
  toggle,
  deal = {},
  department,
  phase = "preAnalytical",
  onSaved = () => {},
}) {
  const { title: phaseTitle, segment } = UTILIZATION_PHASES[phase],
    phaseName = phaseTitle.toLowerCase(),
    // as named on the sidebar: Preanalytical, Analytical, Postanalytical
    phaseMenu = phaseTitle.replace("-A", "a");
  const { token, activePlatform, branches } = useSelector(({ auth }) => auth),
    history = useHistory(),
    [products, setProducts] = useState([]),
    // units a patient can use per brand+variant: on the shelf + what this record took
    [stock, setStock] = useState({}),
    // units already dispensed to this patient (the saved record), per brand+variant
    [held, setHeld] = useState({}),
    // the packs per brand+variant: open (+ left), sealed, expired
    [shelves, setShelves] = useState({}),
    [utilizations, setUtilizations] = useState([]),
    [suppliers, setSuppliers] = useState([]),
    // generics listed: tagged to the tests (and saved ones no longer tagged)
    [generics, setGenerics] = useState([]),
    // units used per brand + variant: { "productId:variantId": qty }
    [qtys, setQtys] = useState({}),
    [search, setSearch] = useState(""),
    [category, setCategory] = useState(""),
    [supplierId, setSupplierId] = useState(""),
    // every tagged product is listed, those without stock marked "No stock
    // available" so staff know not to dispense them
    [inStockOnly, setInStockOnly] = useState(false),
    [recordedBy, setRecordedBy] = useState(null),
    [isLoading, setIsLoading] = useState(false),
    { addToast } = useToasts();

  const { _id: dealId, customerId = {}, cart = [] } = deal;

  // services of the cart that are done by this department
  const services = useMemo(() => {
    const ids = [
      ...new Set(cart.flatMap(({ packages = [] }) => packages.map(Number))),
    ];
    return Services.whereIn(ids).filter(
      ({ department: d }) => d === department,
    );
  }, [cart, department]);

  const suggested = useMemo(
    () => suggestGenerics(services, utilizations, phase),
    [services, utilizations, phase],
  );

  // only those with access (Chief MLS) gets the link to the setup page
  // the setup pages of the phases are siblings, named by their segment
  const setupPath = useMemo(
    () =>
      (show ? getSetupPath(activePlatform, branches) : null)?.replace(
        /[^/]+$/,
        segment,
      ) || null,
    [show, activePlatform, branches, segment],
  );

  const withoutSetup = services.filter(
    ({ id }) =>
      !utilizations.find(({ serviceId }) => serviceId === id)?.phases?.[phase]
        ?.length,
  );

  useEffect(() => {
    if (!show || !dealId) return;

    setIsLoading(true);
    setSearch("");
    setCategory("");
    setSupplierId("");
    setInStockOnly(false);

    const branch = activePlatform.branchId;

    Promise.all([
      axioKit.universal("commerce/merchandise/products/browse", token, {
        branch,
      }),
      axioKit.universal(`${url}/browse`, token, { dealId, department, phase }),
      // the actual inventory packs (what this patient took is already out)
      axioKit.universal(`${PURCHASE_URL}/shelf`, token, { branch }),
      services.length
        ? axioKit.universal(`${UTILIZATION_URL}/browse`, token, {
            branch,
            serviceIds: services.map(({ id }) => id).join(","),
            department: UTILIZATION_DEPARTMENT[department],
          })
        : { payload: [] },
      axioKit
        .universal(SUPPLIERS_URL, token, {
          clients: branch,
          category: "supplier",
        })
        .catch(() => ({ payload: [] })),
    ])
      .then(([_products, _consumption, _stock, _utilizations, _suppliers]) => {
        const _brands = _products?.payload || [];
        const _utils = _utilizations?.payload || [];
        setProducts(_brands);
        setUtilizations(_utils);
        setSuppliers(_suppliers?.payload || []);

        const { items: saved = [], user } = _consumption?.payload || {};
        setRecordedBy(_consumption?.payload ? user || {} : null);

        const _shelves = (_stock?.payload || []).reduce((result, entry) => {
          result[stockKey(entry.product, entry.variant)] = entry;
          return result;
        }, {});
        // dispensed already: a save replaces it, so it can be kept or changed
        const _held = saved.reduce((result, { product, variant, qty }) => {
          if (product) {
            const key = stockKey(product, variant);
            result[key] = (result[key] || 0) + (Number(qty) || 0);
          }
          return result;
        }, {});
        const _available = Object.fromEntries(
          [...new Set([...Object.keys(_shelves), ...Object.keys(_held)])].map(
            (key) => [
              key,
              Math.max(_shelves[key]?.onHand || 0, 0) + (_held[key] || 0),
            ],
          ),
        );
        setShelves(_shelves);
        setHeld(_held);
        setStock(_available);

        const _suggested = suggestGenerics(services, _utils, phase);
        if (_consumption?.payload) {
          // the saved units; generics saved but no longer tagged are listed too
          const extra = saved
            .map(({ pgid, product }) =>
              GenericProducts.find(pgid || product?.pgid),
            )
            .filter(
              (generic) =>
                generic &&
                !_suggested.some((entry) => entry.generic.id === generic.id),
            )
            .filter(
              (generic, index, list) =>
                list.findIndex(({ id }) => id === generic.id) === index,
            )
            .map((generic) => ({
              generic,
              tag: {},
              qty: 1,
              for: [],
              serviceIds: [],
            }));
          setGenerics([..._suggested, ...extra]);
          setQtys(
            saved.reduce((result, { product, variant, qty }) => {
              if (product)
                result[stockKey(product, variant)] = Number(qty) || 0;
              return result;
            }, {}),
          );
        } else {
          setGenerics(_suggested);
          setQtys(defaultQtys(_suggested, _brands, _available));
        }
      })
      .catch((error) =>
        addToast(error.message, { appearance: "error", autoDismiss: true }),
      )
      .finally(() => setIsLoading(false));
    // services is derived from the deal, refetch only when the modal opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, dealId, department, phase, token, activePlatform.branchId]);

  const supplierName = (id) => {
    const found = suppliers.find(({ _id }) => _id === (id?._id || id));
    return found ? found.name || found.displayname : "";
  };

  // every brand + variant of the tagged generics, with its shelf and units used
  const rows = useMemo(
    () =>
      generics.flatMap(({ generic, tag, qty: defaultQty, for: _for = [] }) => {
        const preset = autoSelect(products, generic.id, tag);
        const base = generic.uom === "test" ? "tests" : "pcs";
        return optionsOf(products, generic.id).map(({ product, variant }) => {
          const key = stockKey(product, variant);
          const entry = shelves[key] || {};
          // the pack size registered for the variant (largest breakdown level)
          const registered = packSizeOf(variant, product);
          // the pack size received, else the registered one
          const packQty = Number(entry.packSize || registered);
          const onHand = Math.max(onHandOf(stock, product, variant), 0);
          const qty = Number(qtys[key]) || 0;
          const shelf = shelfView(entry, packQty);
          const dispensed = held[key] || 0;
          return {
            key,
            generic,
            product,
            variant,
            for: _for,
            defaultQty: defaultQty || 1,
            isDefault:
              preset.product?._id === product._id &&
              (preset.variant?._id || null) === (variant?._id || null),
            base,
            packQty,
            unit: packQty > 1 ? packUnitOf(variant, product) || "pack" : base,
            onHand,
            expired: entry.expired || 0,
            // received with fewer units per pack than registered (e.g. 1 pc per box)
            wrongPack:
              !!entry.packSize && entry.packSize < registered ? registered : 0,
            // breakdown levels (box > pack > pc)
            packages: levelsOf(variant, product),
            qty,
            dispensed,
            shelf,
            // only the units added to what was dispensed are taken
            toOpen: packsToOpen(Math.max(qty - dispensed, 0), shelf, packQty),
            supplierIds: [
              product.supplier?._id || product.supplier,
              ...(product.suppliers || []),
            ]
              .filter(Boolean)
              .map(String),
          };
        });
      }),
    [generics, products, stock, shelves, held, qtys],
  );

  const categories = useMemo(
    () =>
      [...new Set(rows.map(({ generic }) => generic.category))]
        .filter(Boolean)
        .sort(),
    [rows],
  );

  const visibleRows = useMemo(() => {
    const key = search.trim().toLowerCase();
    return rows
      .filter(({ onHand, qty }) => !inStockOnly || onHand > 0 || qty > 0)
      .filter(({ generic }) => !category || generic.category === category)
      .filter(
        ({ supplierIds }) => !supplierId || supplierIds.includes(supplierId),
      )
      .filter(
        ({ generic, product, variant }) =>
          !key ||
          [generic.abbreviation, generic.name, product.name, variant?.name]
            .filter(Boolean)
            .some((value) => value.toLowerCase().includes(key)),
      );
  }, [rows, search, category, supplierId, inStockOnly]);

  const used = rows.filter(({ qty }) => qty > 0);
  const insufficient = used.filter(({ qty, onHand }) => qty > onHand);
  const totalUnits = used.reduce((sum, { qty }) => sum + qty, 0);

  // dispensed already: saving again is only for a change of the quantities
  const isDispensed = Object.keys(held).length > 0;
  const isChanged =
    !isDispensed ||
    [...new Set([...Object.keys(held), ...Object.keys(qtys)])].some(
      (key) => (Number(qtys[key]) || 0) !== (held[key] || 0),
    );

  const setQty = (key, value) =>
    setQtys((prev) => ({
      ...prev,
      [key]: Math.max(Math.floor(Number(value)) || 0, 0),
    }));

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const { success, dispensed } = await axioKit.save(
        url,
        {
          branchId: activePlatform.branchId,
          dealId,
          customerId: customerId?._id,
          department,
          phase,
          items: used.map(({ generic, product, variant, qty }) => ({
            pgid: generic.id,
            product: product._id,
            variant: variant?._id,
            qty,
          })),
        },
        token,
      );
      addToast(success, { appearance: "success", autoDismiss: true });
      // deal.hasDispense, shown on the onboarding card
      onSaved(dispensed);
      toggle();
    } catch (error) {
      addToast(error.message, { appearance: "error", autoDismiss: true });
    } finally {
      setIsLoading(false);
    }
  };

  // rendered on body so the card styles do not affect the modal
  return createPortal(
    <MDBModal isOpen={show} toggle={toggle} backdrop size="xl">
      <MDBModalHeader
        toggle={toggle}
        className="light-blue darken-3 white-text"
      >
        <MDBIcon icon="flask" className="mr-2" />
        {phaseTitle} Supplies
      </MDBModalHeader>
      <MDBModalBody style={{ maxHeight: "72vh", overflowY: "auto" }}>
        {/* patient + tests: green with a setup, red without */}
        <div
          className="d-flex flex-wrap align-items-center mb-3"
          style={{ gap: 12 }}
        >
          <span>
            <span className="text-muted">Patient:</span>{" "}
            <b>{fullName(customerId?.fullName)}</b>
          </span>
          <span
            className="border-left pl-3 d-flex flex-wrap align-items-center"
            style={{ gap: 6 }}
          >
            <span className="text-muted mr-1">Tests:</span>
            {!services.length && (
              <small className="text-muted">none for this department</small>
            )}
            {services.map(({ id, abbreviation, name }) => {
              const hasSetup = !withoutSetup.some(
                (service) => service.id === id,
              );
              const count = generics.filter(({ serviceIds = [] }) =>
                serviceIds.includes(id),
              ).length;
              return (
                <MDBBadge
                  key={id}
                  color={isLoading ? "light" : hasSetup ? "success" : "danger"}
                  className="py-1 px-2"
                  title={
                    hasSetup
                      ? `${count} supply(ies) tagged`
                      : `No ${phaseName} supplies set`
                  }
                >
                  {abbreviation || name}
                  {!isLoading && (hasSetup ? ` - ${count}` : " - not set")}
                </MDBBadge>
              );
            })}
          </span>
        </div>

        {!isLoading && withoutSetup.length > 0 && (
          <div className="alert alert-danger py-1 px-2 small mb-2">
            <MDBIcon icon="exclamation-triangle" className="mr-1" />
            No {phaseName} setup yet:{" "}
            <b>
              {withoutSetup
                .map(({ abbreviation, name }) => abbreviation || name)
                .join(", ")}
            </b>
            .{" "}
            {setupPath ? (
              <>
                Go to:{" "}
                <a
                  href={setupPath}
                  className="font-weight-bold"
                  onClick={(e) => {
                    e.preventDefault();
                    toggle();
                    history.push(
                      `${setupPath}?department=${department}&service=${withoutSetup[0].id}`,
                    );
                  }}
                >
                  Supply Management &gt; Consumables &gt; {phaseMenu}
                  <MDBIcon icon="external-link-alt" className="ml-1" />
                </a>
              </>
            ) : (
              <>
                Please ask your <b>Chief Medical Technologist</b> to set it in
                Supply Management &gt; Consumables &gt; {phaseMenu}.
              </>
            )}
          </div>
        )}

        {/* the record replaces itself: saving again never takes twice */}
        {!isLoading && Object.keys(held).length > 0 && (
          <div className="alert alert-success py-1 px-2 small mb-2">
            <MDBIcon icon="check-circle" className="mr-1" />
            Already dispensed for this patient:{" "}
            <b>
              {Object.values(held).reduce((sum, qty) => sum + qty, 0)} unit(s)
            </b>
            , already deducted from the inventory (Available is what is left).
            Saving again only takes the added units, or returns the removed
            ones.
          </div>
        )}

        {/* tagged, but nothing in the inventory yet (P.O. not received / not synced) */}
        {!isLoading &&
          rows.length > 0 &&
          rows.every(({ onHand }) => !onHand) && (
            <div className="alert alert-warning py-1 px-2 small mb-2">
              <MDBIcon icon="box-open" className="mr-1" />
              The tagged products have no stock in the inventory yet. Receive
              their purchase order, or open Purchase Orders &gt; Received and
              press <b>Sync inventory</b> on the P.O.s received before.
            </div>
          )}

        {!isLoading && insufficient.length > 0 && (
          <div className="alert alert-danger py-1 px-2 small mb-2">
            <MDBIcon icon="box-open" className="mr-1" />
            Not enough stock for{" "}
            <b>
              {insufficient
                .map(({ product, variant }) =>
                  [product.name, variant?.name].filter(Boolean).join(" "),
                )
                .join(", ")}
            </b>
            . Lower the quantity or use another brand/variant.
          </div>
        )}

        {/* filters */}
        <div
          className="d-flex flex-wrap align-items-center mb-2"
          style={{ gap: 8 }}
        >
          <div
            className="position-relative"
            style={{ flex: "1 1 260px", maxWidth: 420 }}
          >
            <MDBIcon
              icon="search"
              className="position-absolute text-muted"
              style={{ top: "50%", left: 12, transform: "translateY(-50%)" }}
            />
            <input
              className="form-control"
              style={{ paddingLeft: 36 }}
              placeholder="Search product, brand, or code..."
              value={search}
              onChange={({ target }) => setSearch(target.value)}
            />
          </div>
          <select
            className="browser-default custom-select"
            style={{ width: 180 }}
            value={category}
            onChange={({ target }) => setCategory(target.value)}
          >
            <option value="">All categories</option>
            {categories.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
          <select
            className="browser-default custom-select"
            style={{ width: 200 }}
            value={supplierId}
            onChange={({ target }) => setSupplierId(target.value)}
          >
            <option value="">All suppliers</option>
            {suppliers.map(({ _id, name, displayname }) => (
              <option key={_id} value={_id}>
                {name || displayname}
              </option>
            ))}
          </select>
          <select
            className="browser-default custom-select"
            style={{ width: 170 }}
            value={inStockOnly ? "stock" : "all"}
            onChange={({ target }) => setInStockOnly(target.value === "stock")}
          >
            <option value="all">All tagged products</option>
            <option value="stock">In stock only</option>
          </select>
        </div>

        <MDBTable small hover responsive className="mb-0">
          <thead className="grey lighten-4">
            <tr className="small">
              <th style={{ width: 36 }} />
              <th>Code</th>
              <th>
                Product / Brand / Variant
                <small className="d-block text-muted">
                  (Generic · Supplier)
                </small>
              </th>
              <th>Unit</th>
              <th className="text-center">
                Available
                <small className="d-block text-muted">(total)</small>
              </th>
              <th className="text-center">
                In Use
                <small className="d-block text-muted">(opened)</small>
              </th>
              <th className="text-center">Remaining</th>
              <th style={{ width: 170 }}>Qty Used</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && !rows.length && (
              <tr>
                <td colSpan={8} className="text-center text-muted py-3">
                  Loading...
                </td>
              </tr>
            )}
            {!isLoading && !visibleRows.length && (
              <tr>
                <td colSpan={8} className="text-center text-muted py-3">
                  {!rows.length
                    ? "No supplies tagged to these tests."
                    : inStockOnly && rows.every(({ onHand }) => !onHand)
                      ? "None of the tagged products is in the inventory."
                      : "No supply matches the filters."}
                </td>
              </tr>
            )}
            {visibleRows.map((row) => {
              const checked = row.qty > 0;
              const tooMany = row.qty > row.onHand;
              const supplier =
                supplierName(row.product.supplier) ||
                supplierName(row.product.suppliers?.[0]);
              return (
                <tr
                  key={row.key}
                  style={{ opacity: row.onHand || checked ? 1 : 0.55 }}
                >
                  <td className="align-middle text-center">
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={isLoading || (!row.onHand && !checked)}
                      onChange={({ target }) =>
                        setQty(
                          row.key,
                          target.checked
                            ? Math.min(
                                row.isDefault ? row.defaultQty : 1,
                                row.onHand,
                              )
                            : 0,
                        )
                      }
                    />
                  </td>
                  <td className="align-middle">
                    <MDBBadge color="light" className="text-danger">
                      {row.generic.abbreviation}
                    </MDBBadge>
                  </td>
                  <td className="align-middle">
                    {row.product.name}
                    {row.variant && ` · ${row.variant.name}`}
                    {row.isDefault && (
                      <MDBIcon
                        icon="star"
                        className="text-warning ml-1"
                        title="Default"
                      />
                    )}
                    <small className="d-block text-muted">
                      {row.generic.name}
                      {supplier ? ` · ${supplier}` : ""}
                      {row.for.length ? ` · for ${row.for.join(", ")}` : ""}
                    </small>
                  </td>
                  {/* every quantity in units (pcs / tests); packs are shown under it */}
                  <td className="align-middle text-muted">
                    {row.base}
                    {row.packQty > 1 && (
                      <small className="d-block">
                        1 {row.unit} = {row.packQty}
                      </small>
                    )}
                    {row.wrongPack > 0 && (
                      <small
                        className="d-block text-danger"
                        title="Fix it in Inventory Records > Set pack size"
                      >
                        received as {row.packQty} per pack, should be{" "}
                        {row.wrongPack}
                      </small>
                    )}
                  </td>
                  <td className="align-middle text-center text-primary font-weight-bold">
                    {row.shelf.available}
                    {row.packages.length > 1 && row.shelf.available > 0 && (
                      <small className="d-block text-muted font-weight-normal">
                        {breakdownOf(row.shelf.available, row.packages)}
                      </small>
                    )}
                    {/* shown so staff know not to dispense it */}
                    {!row.shelf.available && (
                      <MDBBadge color="danger" className="d-block mt-1">
                        No stock available
                      </MDBBadge>
                    )}
                    {/* expired packs cannot be used on a patient */}
                    {row.expired > 0 && (
                      <small className="d-block text-danger font-weight-normal">
                        {row.expired} expired
                      </small>
                    )}
                  </td>
                  <td className="align-middle text-center text-warning font-weight-bold">
                    {row.shelf.inUse}
                    {row.packQty > 1 && (
                      <small className="d-block text-muted font-weight-normal">
                        {row.shelf.openPacks} {row.unit} open
                      </small>
                    )}
                  </td>
                  <td
                    className={`align-middle text-center font-weight-bold ${
                      row.shelf.remaining ? "text-success" : "text-danger"
                    }`}
                  >
                    {row.shelf.remaining}
                    {row.packQty > 1 && (
                      <small className="d-block text-muted font-weight-normal">
                        {row.shelf.sealedPacks} {row.unit} sealed
                      </small>
                    )}
                  </td>
                  <td className="align-middle">
                    <div className="d-flex align-items-center">
                      <input
                        type="number"
                        min={0}
                        step={1}
                        className={`form-control form-control-sm ${
                          tooMany ? "is-invalid" : ""
                        }`}
                        style={{ width: 80 }}
                        value={row.qty}
                        disabled={isLoading || (!row.onHand && !checked)}
                        onChange={({ target }) => setQty(row.key, target.value)}
                      />
                      <small className="text-muted ml-2">{row.base}</small>
                    </div>
                    {row.dispensed > 0 && (
                      <small
                        className={`d-block ${
                          row.qty === row.dispensed
                            ? "text-success"
                            : "text-warning"
                        }`}
                      >
                        <MDBIcon icon="check" className="mr-1" />
                        {row.dispensed} {row.base} dispensed
                        {row.qty > row.dispensed &&
                          `, +${row.qty - row.dispensed} more`}
                        {row.qty < row.dispensed &&
                          `, ${row.dispensed - row.qty} returned`}
                      </small>
                    )}
                    {/* units come from the opened pack first */}
                    {checked &&
                      row.packQty > 1 &&
                      !tooMany &&
                      row.qty > row.dispensed && (
                        <small
                          className={`d-block ${
                            row.toOpen ? "text-warning" : "text-muted"
                          }`}
                        >
                          {row.toOpen
                            ? `${row.shelf.openLeft ? "open one used up, " : ""}opens ${row.toOpen} sealed ${row.unit} (first received)`
                            : `from the open ${row.unit}`}
                        </small>
                      )}
                    {tooMany && (
                      <small className="d-block text-danger">
                        only {row.onHand} left
                        {row.dispensed ? ` (${row.dispensed} dispensed)` : ""}
                      </small>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </MDBTable>

        {recordedBy && (
          <small className="text-muted d-block mt-2">
            <MDBIcon icon="check" className="text-success mr-1" />
            Saved
            {recordedBy.fullName ? ` by ${fullName(recordedBy.fullName)}` : ""}
          </small>
        )}
      </MDBModalBody>
      <MDBModalFooter className="py-2">
        <div
          className="mr-auto px-3 py-2 rounded"
          style={{ background: "#eef8ef", color: "#2e7d32" }}
        >
          <MDBIcon icon="shopping-cart" className="mr-2" />
          <b>{totalUnits}</b> unit(s) used
        </div>
        {suggested.length > 0 && (
          <MDBBtn
            color="light"
            onClick={() => {
              setGenerics(suggested);
              setQtys(defaultQtys(suggested, products, stock));
            }}
            disabled={isLoading}
            title="Back to the default quantities of the setup"
          >
            <MDBIcon icon="undo" className="mr-1" /> Use defaults
          </MDBBtn>
        )}
        <MDBBtn color="danger" onClick={toggle} disabled={isLoading}>
          Cancel
        </MDBBtn>
        <MDBBtn
          color="primary"
          onClick={handleSave}
          disabled={isLoading || insufficient.length > 0 || !isChanged}
          title={
            insufficient.length
              ? "Not enough stock for some supplies"
              : !isChanged
                ? "Already dispensed, change a quantity to update it"
                : undefined
          }
        >
          <MDBIcon
            icon={isChanged ? "check" : "check-double"}
            className="mr-1"
          />
          {!isDispensed ? "Save" : isChanged ? "Update dispense" : "Dispensed"}
        </MDBBtn>
      </MDBModalFooter>
    </MDBModal>,
    document.body,
  );
}
